/**
 * Einstieg der Oberfläche.
 *
 * Vor allem anderen wird der Zustand der Anmeldung geladen. Ist die
 * Einrichtung offen, erscheint das Einrichtungsformular, sonst die Anmeldung.
 * Erst danach sind die Bereiche erreichbar.
 */
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from './Layout.tsx';
import { AppIcon } from './components/AppIcon.tsx';
import { AuthPage } from './pages/AuthPage.tsx';
import { FavoritesPage } from './pages/FavoritesPage.tsx';
import { RecipeDetailPage } from './pages/RecipeDetailPage.tsx';
import { RecipeEditorPage } from './pages/RecipeEditorPage.tsx';
import { RecipeListPage } from './pages/RecipeListPage.tsx';
import { SearchPage } from './pages/SearchPage.tsx';
import { SettingsPage } from './pages/SettingsPage.tsx';
import { ShoppingPage } from './pages/ShoppingPage.tsx';
import { CookbooksPage } from './pages/CookbooksPage.tsx';
import { ProductsPage } from './pages/ProductsPage.tsx';
import { CookModePage } from './pages/CookModePage.tsx';
import { useAuth } from './state/auth.ts';
import { texts } from './texts/de.ts';

/** Wartebildschirm beim Laden. */
function LoadingScreen() {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-3 bg-surface text-content-muted">
      <AppIcon name="hat" size={40} className="text-accent" />
      <p>{texts.common.loading}</p>
    </div>
  );
}

export function App() {
  const { loading, setupRequired, user, refresh } = useAuth();

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (user === null) {
    return (
      <BrowserRouter>
        <AuthPage setupRequired={setupRequired} />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Der Kochmodus läuft ohne Navigation, damit nichts ablenkt. */}
        <Route path="/rezepte/:id/kochen" element={<CookModePage />} />
        <Route element={<AppLayout />}>
          <Route path="/rezepte" element={<RecipeListPage />} />
          <Route path="/rezepte/neu" element={<RecipeEditorPage />} />
          <Route path="/rezepte/:id" element={<RecipeDetailPage />} />
          <Route path="/rezepte/:id/bearbeiten" element={<RecipeEditorPage />} />
          <Route path="/merkliste" element={<FavoritesPage />} />
          <Route path="/suche" element={<SearchPage />} />
          <Route path="/einkauf" element={<ShoppingPage />} />
          <Route path="/einkauf/vorrat" element={<ShoppingPage tab="pantry" />} />
          <Route path="/einkauf/laeden" element={<ShoppingPage tab="stores" />} />
          <Route path="/produkte" element={<ProductsPage />} />
          <Route path="/produkte/:id" element={<ProductsPage />} />
          <Route path="/kochbuecher" element={<CookbooksPage />} />
          <Route path="/kochbuecher/:id" element={<CookbooksPage />} />
          <Route path="/einstellungen" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/rezepte" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
